// frontend/src/screens/admin/WardPreShiftCreateScreen.jsx
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form, Button, Row, Col, Card, Tabs, Tab, Container, Table, Modal } from 'react-bootstrap';
import Message from '../../components/Message';
import Loader from '../../components/Loader';
import { toast } from 'react-toastify';
import { FaTrash } from 'react-icons/fa';
import { useSelector } from 'react-redux';

import {
  CATEGORY_OF_STAFF,
  REASON_FOR_REQUEST,
} from '../../utils/enums';

import { useCreatePreShiftMutation } from '../../slices/preShiftApiSlice';
import { useGetHospitalLocationsQuery } from '../../slices/hospitalLocationApiSlice';

// Shift time presets
const SHIFT_PRESETS = {
  'LD': { start: '08:00', end: '20:00', label: 'Long Day (8:00-20:00)' },
  'ND': { start: '20:00', end: '08:00', label: 'Night Duty (20:00-8:00)' },
  'AM': { start: '08:00', end: '16:00', label: 'Morning (8:00-16:00)' },
  'PM': { start: '14:00', end: '20:00', label: 'Afternoon (14:00-20:00)' },
  'TW': { start: '20:00', end: '00:00', label: 'Twilight (20:00-00:00)' },
  'ELD': { start: '07:30', end: '20:30', label: 'Extended Long Day (7:30-20:30)' },
  'ESCORT': { start: '08:00', end: '20:00', label: 'Escort (8:00-20:00)' },
};

// Recurrence options
const RECURRENCE_OPTIONS = {
  NONE: { label: 'No Recurrence', value: 'NONE' },
  DAILY: { label: 'Daily', value: 'DAILY' },
  WEEKLY: { label: 'Weekly', value: 'WEEKLY' },
  BIWEEKLY: { label: 'Every 2 Weeks', value: 'BIWEEKLY' },
  MONTHLY: { label: 'Monthly', value: 'MONTHLY' },
};

// Days of week for recurrence
const DAYS_OF_WEEK = [
  { value: 'MON', label: 'Monday' },
  { value: 'TUE', label: 'Tuesday' },
  { value: 'WED', label: 'Wednesday' },
  { value: 'THU', label: 'Thursday' },
  { value: 'FRI', label: 'Friday' },
  { value: 'SAT', label: 'Saturday' },
  { value: 'SUN', label: 'Sunday' },
];

const WardPreShiftCreateScreen = () => {
  const navigate = useNavigate();
  const { userInfo } = useSelector((state) => state.auth);

  // Form state
  const [hospitalLocationId, setHospitalLocationId] = useState('');
  const [wardDepartmentArea, setWardDepartmentArea] = useState('');
  const [reasonForRequest, setReasonForRequest] = useState('');
  const [reasonForRequestDetails, setReasonForRequestDetails] = useState('');
  const [categoryOfStaff, setCategoryOfStaff] = useState('');
  const [escort, setEscort] = useState(false);
  const [special, setSpecial] = useState(false);
  const [isOvertime, setIsOvertime] = useState(false);
  const [eccNumber, setEccNumber] = useState('');
  const [specialRequirements, setSpecialRequirements] = useState('');
  const [comments, setComments] = useState('');
  const [numberOfShifts, setNumberOfShifts] = useState(1);

  // Date and time
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [startTime, setStartTime] = useState('08:00');
  const [endTime, setEndTime] = useState('16:00');
  const [selectedPreset, setSelectedPreset] = useState('AM');

  // Recurrence settings
  const [recurrenceType, setRecurrenceType] = useState('NONE');
  const [recurrenceEndDate, setRecurrenceEndDate] = useState('');
  const [recurrenceOccurrences, setRecurrenceOccurrences] = useState(1);
  const [selectedDays, setSelectedDays] = useState(['MON', 'TUE', 'WED', 'THU', 'FRI']);

  // Modal state
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [shiftList, setShiftList] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState('basic');

  const {
    data: hospitalLocations,
    isLoading: loadingLocations,
    error: errorLocations,
  } = useGetHospitalLocationsQuery();

  const [createPreShift, { isLoading: loadingCreate }] = useCreatePreShiftMutation();

  // Initialize dates when component mounts
  useEffect(() => {
    const today = new Date();
    const todayStr = today.toISOString().split('T')[0];
    setStartDate(todayStr);
    setEndDate(todayStr);
    
    // Set recurrence end date to 4 weeks from now
    const futureDate = new Date(today);
    futureDate.setDate(futureDate.getDate() + 28);
    setRecurrenceEndDate(futureDate.toISOString().split('T')[0]);
  }, []);

  // Update end date when start date changes
  useEffect(() => {
    if (startDate && !endDate) {
      setEndDate(startDate);
    }
  }, [startDate, endDate]);

  // Apply preset times when selected
  useEffect(() => {
    const preset = SHIFT_PRESETS[selectedPreset];
    if (preset) {
      setStartTime(preset.start);
      setEndTime(preset.end);
      
      // Update escort checkbox based on preset
      if (selectedPreset === 'ESCORT') {
        setEscort(true);
      }
    }
  }, [selectedPreset]);

  // Handle day selection for recurrence
  const handleDayToggle = (dayValue) => {
    if (selectedDays.includes(dayValue)) {
      setSelectedDays(selectedDays.filter(day => day !== dayValue));
    } else {
      setSelectedDays([...selectedDays, dayValue]);
    }
  };

  // Generate all shift dates based on recurrence settings
  // Generate all shift dates based on recurrence settings
const generateShiftDates = () => {
  const shifts = [];
  const start = new Date(startDate);
  
  console.log('Generating shift dates with:', {
    recurrenceType,
    selectedDays,
    numberOfShifts,
    startDate,
    startTime,
    endTime,
    recurrenceEndDate,
    recurrenceOccurrences
  });
  
  // For NON-RECURRING shifts
  if (recurrenceType === 'NONE') {
    for (let i = 0; i < numberOfShifts; i++) {
      shifts.push({
        id: `${startDate}-${startTime}-${endTime}-${i}-${Date.now()}`,
        startDate: startDate,
        endDate: endDate,
        startTime,
        endTime,
        shiftNumber: i + 1
      });
    }
    return shifts;
  }
  
  // Map day values for weekly recurrences
  const dayMap = {
    'SUN': 0, 'MON': 1, 'TUE': 2, 'WED': 3, 
    'THU': 4, 'FRI': 5, 'SAT': 6
  };
  
  // Day names for debugging
  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  
  // Determine which day numbers to use based on recurrence type
  let selectedDayNumbers;
  
  if (recurrenceType === 'DAILY') {
    // For daily recurrence, use the selected days to filter
    selectedDayNumbers = selectedDays.map(day => dayMap[day]);
    console.log('Daily recurrence with selected days:', selectedDayNumbers.map(num => dayNames[num]));
  } else if (recurrenceType === 'WEEKLY' || recurrenceType === 'BIWEEKLY') {
    // For weekly recurrence, use selected days
    selectedDayNumbers = selectedDays.map(day => dayMap[day]);
  } else {
    // For MONTHLY recurrence, include all days
    selectedDayNumbers = [0, 1, 2, 3, 4, 5, 6];
  }
  
  let totalOccurrences = 0;
  const maxOccurrences = parseInt(recurrenceOccurrences);
  const recurrenceEnd = recurrenceEndDate ? new Date(recurrenceEndDate) : null;
  
  // DAILY recurrence with day filtering
  if (recurrenceType === 'DAILY') {
    let currentDate = new Date(start);
    
    while (true) {
      if (maxOccurrences > 0 && totalOccurrences >= maxOccurrences) break;
      if (recurrenceEnd && currentDate > recurrenceEnd) break;
      
      const currentDayOfWeek = currentDate.getDay(); // 0 = Sunday, 1 = Monday, etc.
      const dateStr = currentDate.toISOString().split('T')[0];
      
      console.log(`Checking ${dateStr} (${dayNames[currentDayOfWeek]}): Day number ${currentDayOfWeek}, Selected days: [${selectedDayNumbers.join(', ')}]`);
      
      // FIX: Only create shifts if current day is in selected days
      if (selectedDayNumbers.includes(currentDayOfWeek)) {
        console.log(`✓ Creating shift(s) for ${dateStr} (${dayNames[currentDayOfWeek]}) - Selected day`);
        
        for (let i = 0; i < numberOfShifts; i++) {
          shifts.push({
            id: `${dateStr}-${startTime}-${endTime}-${i}-${totalOccurrences}-${Date.now()}`,
            startDate: dateStr,
            endDate: dateStr,
            startTime,
            endTime,
            dayIndex: i + 1
          });
          totalOccurrences++;
          
          if (maxOccurrences > 0 && totalOccurrences >= maxOccurrences) break;
        }
      } else {
        console.log(`✗ Skipping ${dateStr} (${dayNames[currentDayOfWeek]}) - not in selected days`);
      }
      
      currentDate.setDate(currentDate.getDate() + 1);
      if (totalOccurrences > 1000) break; // Safety limit
    }
  }
  
  // WEEKLY recurrence
  else if (recurrenceType === 'WEEKLY') {
    let currentWeekStart = new Date(start);
    let weekCount = 0;
    
    while (true) {
      if (maxOccurrences > 0 && totalOccurrences >= maxOccurrences) break;
      if (recurrenceEnd && currentWeekStart > recurrenceEnd) break;
      
      for (const dayNumber of selectedDayNumbers) {
        const dayDate = new Date(currentWeekStart);
        const currentDayOfWeek = dayDate.getDay();
        
        // FIX: Handle day calculation properly
        let daysToAdd = dayNumber - currentDayOfWeek;
        if (daysToAdd < 0) {
          daysToAdd += 7; // Add a week if we're going backwards
        }
        
        dayDate.setDate(dayDate.getDate() + daysToAdd);
        
        if (dayDate < start) continue;
        if (maxOccurrences > 0 && totalOccurrences >= maxOccurrences) break;
        if (recurrenceEnd && dayDate > recurrenceEnd) break;
        
        const dateStr = dayDate.toISOString().split('T')[0];
        
        for (let i = 0; i < numberOfShifts; i++) {
          shifts.push({
            id: `${dateStr}-${startTime}-${endTime}-${i}-${totalOccurrences}-${Date.now()}`,
            startDate: dateStr,
            endDate: dateStr,
            startTime,
            endTime,
            week: weekCount + 1,
            dayIndex: i + 1
          });
          totalOccurrences++;
          
          if (maxOccurrences > 0 && totalOccurrences >= maxOccurrences) break;
        }
        
        if (maxOccurrences > 0 && totalOccurrences >= maxOccurrences) break;
      }
      
      currentWeekStart.setDate(currentWeekStart.getDate() + 7);
      weekCount++;
      if (weekCount > 100) break; // Safety limit
    }
  }
  
  // BIWEEKLY recurrence
  else if (recurrenceType === 'BIWEEKLY') {
    let currentWeekStart = new Date(start);
    let weekCount = 0;
    
    while (true) {
      if (maxOccurrences > 0 && totalOccurrences >= maxOccurrences) break;
      if (recurrenceEnd && currentWeekStart > recurrenceEnd) break;
      
      // Only process every 2 weeks
      if (weekCount % 2 === 0) {
        for (const dayNumber of selectedDayNumbers) {
          const dayDate = new Date(currentWeekStart);
          const currentDayOfWeek = dayDate.getDay();
          
          // FIX: Handle day calculation properly
          let daysToAdd = dayNumber - currentDayOfWeek;
          if (daysToAdd < 0) {
            daysToAdd += 7; // Add a week if we're going backwards
          }
          
          dayDate.setDate(dayDate.getDate() + daysToAdd);
          
          if (dayDate < start) continue;
          if (maxOccurrences > 0 && totalOccurrences >= maxOccurrences) break;
          if (recurrenceEnd && dayDate > recurrenceEnd) break;
          
          const dateStr = dayDate.toISOString().split('T')[0];
          
          for (let i = 0; i < numberOfShifts; i++) {
            shifts.push({
              id: `${dateStr}-${startTime}-${endTime}-${i}-${totalOccurrences}-${Date.now()}`,
              startDate: dateStr,
              endDate: dateStr,
              startTime,
              endTime,
              week: Math.floor(weekCount / 2) + 1,
              dayIndex: i + 1
            });
            totalOccurrences++;
            
            if (maxOccurrences > 0 && totalOccurrences >= maxOccurrences) break;
          }
          
          if (maxOccurrences > 0 && totalOccurrences >= maxOccurrences) break;
        }
      }
      
      currentWeekStart.setDate(currentWeekStart.getDate() + 7);
      weekCount++;
      if (weekCount > 200) break; // Safety limit
    }
  }
  
  // MONTHLY recurrence
  else if (recurrenceType === 'MONTHLY') {
    let currentDate = new Date(start);
    let monthCount = 0;
    
    while (true) {
      if (maxOccurrences > 0 && totalOccurrences >= maxOccurrences) break;
      if (recurrenceEnd && currentDate > recurrenceEnd) break;
      
      const dateStr = currentDate.toISOString().split('T')[0];
      
      for (let i = 0; i < numberOfShifts; i++) {
        shifts.push({
          id: `${dateStr}-${startTime}-${endTime}-${i}-${totalOccurrences}-${Date.now()}`,
          startDate: dateStr,
          endDate: dateStr,
          startTime,
          endTime,
          monthIndex: monthCount + 1,
          dayIndex: i + 1
        });
        totalOccurrences++;
        
        if (maxOccurrences > 0 && totalOccurrences >= maxOccurrences) break;
      }
      
      currentDate.setMonth(currentDate.getMonth() + 1);
      monthCount++;
      if (monthCount > 60) break; // Safety limit
    }
  }
  
  console.log(`Generated ${shifts.length} shifts:`, shifts.map(s => `${s.startDate} ${s.startTime}-${s.endTime}`));
  
  return shifts;
};

  // Calculate total shifts that will be created
  const calculateTotalShifts = () => {
    const shiftDates = generateShiftDates();
    return shiftDates.length;
  };

  // Sort shifts by date
  const sortShiftsByDate = (shifts) => {
    return [...shifts].sort((a, b) => {
      const dateCompare = a.startDate.localeCompare(b.startDate);
      if (dateCompare !== 0) return dateCompare;
      return a.startTime.localeCompare(b.startTime);
    });
  };

  // Prepare shifts for confirmation
  const prepareShiftsForConfirmation = () => {
    const shifts = generateShiftDates();
    
    const shiftsWithIds = shifts.map((shift, index) => ({
      ...shift,
      id: shift.id || `${shift.startDate}-${shift.startTime}-${shift.endTime}-${index}-${Date.now()}`
    }));
    
    const sortedShifts = sortShiftsByDate(shiftsWithIds);
    setShiftList(sortedShifts);
    setShowConfirmation(true);
  };

  // Validate form before submission
  const validateForm = () => {
    const requiredFields = [
      { value: hospitalLocationId, message: 'Please select a hospital location' },
      { value: categoryOfStaff, message: 'Please select category of staff' },
      { value: reasonForRequest, message: 'Please select reason for request' },
      { value: startDate, message: 'Please select start date' },
      { value: reasonForRequestDetails, message: 'Please provide reason details' },
    ];
    
    for (const field of requiredFields) {
      if (!field.value) {
        toast.error(field.message);
        return false;
      }
    }
    
    // Validate date range
    const start = new Date(startDate);
    const end = new Date(endDate);
    if (end < start) {
      toast.error('End date cannot be before start date');
      return false;
    }
    
    // Validate time
    const startDateTime = new Date(`${startDate}T${startTime}`);
    const endDateTime = new Date(`${endDate}T${endTime}`);
    
    // Check if it's a valid overnight shift
    const isOvernight = startTime > endTime && startDate === endDate;
    
    if (!isOvernight && endDateTime <= startDateTime) {
      toast.error('End time must be after start time');
      return false;
    }
    
    const totalShifts = calculateTotalShifts();
    if (totalShifts > 50) {
      toast.error(`Cannot create more than 50 shift requests at once. You have ${totalShifts} shifts.`);
      return false;
    }
    
    return true;
  };

  const submitHandler = async (e) => {
    e.preventDefault();

    if (isSubmitting) {
      toast.warning('Please wait, form is already submitting...');
      return;
    }
    
    if (!validateForm()) {
      return;
    }
    
    // Show confirmation modal with all shifts
    prepareShiftsForConfirmation();
  };

  // Handle removing a shift from the list
  const handleRemoveShift = (index) => {
    const updatedShifts = [...shiftList];
    updatedShifts.splice(index, 1);
    setShiftList(updatedShifts);
    toast.info(`Shift removed. ${updatedShifts.length} shifts remaining.`);
  };

  // Handle shift update (for editing in modal)
  const handleShiftUpdate = (index, field, value) => {
    const updatedShifts = [...shiftList];
    
    updatedShifts[index] = {
      ...updatedShifts[index],
      [field]: value
    };
    
    // Handle overnight shifts
    if (field === 'startTime' || field === 'endTime') {
      const shift = updatedShifts[index];
      if (shift.startTime > shift.endTime && shift.startDate === shift.endDate) {
        // This is an overnight shift - keep as is
      }
    }
    
    const sortedShifts = sortShiftsByDate(updatedShifts);
    setShiftList(sortedShifts);
  };

  // Calculate duration for display
  const calculateDuration = (startDate, startTime, endDate, endTime) => {
    const start = new Date(`${startDate}T${startTime}`);
    let end = new Date(`${endDate}T${endTime}`);
    
    if (end < start && startDate === endDate) {
      const adjustedEnd = new Date(end);
      adjustedEnd.setDate(adjustedEnd.getDate() + 1);
      end = adjustedEnd;
    }
    
    const durationMs = end - start;
    const hours = Math.floor(durationMs / (1000 * 60 * 60));
    const minutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours === 0 && minutes === 0) return '0h';
    if (minutes === 0) return `${hours}h`;
    return `${hours}h ${minutes}m`;
  };

  // Get day of week
  const getDayOfWeek = (dateStr) => {
    const date = new Date(dateStr);
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    return days[date.getDay()];
  };

  // Check if shift is overnight
  const isOvernightShift = (shift) => {
    return shift.startTime > shift.endTime && shift.startDate === shift.endDate;
  };

  // Check if shift is valid
  const isShiftValid = (shift) => {
    const start = new Date(`${shift.startDate}T${shift.startTime}`);
    const end = new Date(`${shift.endDate}T${shift.endTime}`);
    
    if (end < start && shift.startDate === shift.endDate) {
      const adjustedEnd = new Date(end);
      adjustedEnd.setDate(adjustedEnd.getDate() + 1);
      return adjustedEnd > start;
    }
    
    return end > start;
  };

  // Create all pre-shift requests
  const createAllPreShifts = async () => {
    setIsSubmitting(true);
    
    try {
      const successfulShifts = [];
      const failedShifts = [];
      
      for (let i = 0; i < shiftList.length; i++) {
        const shift = shiftList[i];
        
        // Handle overnight shifts
        let endDateForShift = shift.endDate;
        const startDateTime = new Date(`${shift.startDate}T${shift.startTime}`);
        const endDateTime = new Date(`${shift.endDate}T${shift.endTime}`);
        
        if (endDateTime < startDateTime && shift.startDate === shift.endDate) {
          const tempDate = new Date(shift.endDate);
          tempDate.setDate(tempDate.getDate() + 1);
          endDateForShift = tempDate.toISOString().split('T')[0];
        }

        const shiftData = {
          startDateTime: new Date(`${shift.startDate}T${shift.startTime}`).toISOString(),
          endDateTime: new Date(`${endDateForShift}T${shift.endTime}`).toISOString(),
          hospitalLocationId,
          wardDepartmentArea,
          reasonForRequest,
          reasonForRequestDetails,
          categoryOfStaff,
          escort,
          special,
          isOvertime,
          eccNumber: eccNumber || undefined,
          specialRequirements,
          comments,
        };
        
        try {
          const result = await createPreShift(shiftData).unwrap();
          successfulShifts.push(result);
          console.log(`Created pre-shift ${i + 1}/${shiftList.length}`);
        } catch (err) {
          console.error(`Failed to create pre-shift ${i + 1}:`, err);
          failedShifts.push({ index: i + 1, error: err });
        }
        
        // Small delay between requests
        if (i < shiftList.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }
      
      if (successfulShifts.length > 0) {
        toast.success(`Successfully created ${successfulShifts.length} pre-shift request${successfulShifts.length > 1 ? 's' : ''}!`);
        
        if (failedShifts.length > 0) {
          toast.warning(`${failedShifts.length} request${failedShifts.length > 1 ? 's' : ''} failed to create.`);
        }
        
        setTimeout(() => {
          setShowConfirmation(false);
          navigate('/admin/ward-preshifts');
        }, 2000);
      } else {
        toast.error('Failed to create any pre-shift requests. Please try again.');
        setIsSubmitting(false);
      }
      
    } catch (err) {
      console.error('Error in createAllPreShifts:', err);
      toast.error(err?.data?.message || err.error || 'Failed to create pre-shift requests');
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Container fluid="lg">
        {loadingCreate && <Loader />}
        {loadingLocations ? (
          <Loader />
        ) : errorLocations ? (
          <Message variant="danger">
            {errorLocations?.data?.message || errorLocations.error}
          </Message>
        ) : (
          <Form onSubmit={submitHandler}>
            <Card className="mb-4 custom-card-bg">
              <Card.Header className="fw-bold fs-4">
                Create Pre-Shift Request{calculateTotalShifts() > 1 ? `s (${calculateTotalShifts()} total)` : ''}
              </Card.Header>
              <Card.Body>
                <Tabs
                  activeKey={activeTab}
                  onSelect={(k) => setActiveTab(k)}
                  className="mb-4"
                >
                  <Tab eventKey="basic" title="Basic Information">
                    {/* Top Row: Hospital Location, Category, Reason */}
                    <Row className="mb-4">
                      <Col lg={4}>
                        <Form.Group controlId="isOvertime" className="mb-3">
                          <Form.Check
                            type="checkbox"
                            label={<span className="fw-bold">Overtime Shift</span>}
                            checked={isOvertime}
                            onChange={(e) => setIsOvertime(e.target.checked)}
                          />
                        </Form.Group>
                      </Col>

                      <Col lg={4}>
                        <Form.Group controlId="hospitalLocationId">
                          <Form.Label className="fw-bold">Hospital Location</Form.Label>
                          <Form.Select
                            value={hospitalLocationId}
                            onChange={(e) => {
                              const selectedId = e.target.value;
                              setHospitalLocationId(selectedId);
                              const selectedLocation = hospitalLocations.find(
                                (loc) => loc._id === selectedId
                              );
                              setWardDepartmentArea(selectedLocation?.wardDepartmentArea || '');
                            }}
                            required
                            size="lg"
                          >
                            <option value="">Select Hospital Location...</option>
                            {hospitalLocations.map((location) => (
                              <option key={location._id} value={location._id}>
                                {location.wardDepartmentArea} ({location.department})
                              </option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>

                      <Col lg={4}>
                        <Form.Group controlId="categoryOfStaff">
                          <Form.Label className="fw-bold">Category of Staff</Form.Label>
                          <Form.Select
                            value={categoryOfStaff}
                            onChange={(e) => setCategoryOfStaff(e.target.value)}
                            required
                            size="lg"
                          >
                            <option value="">Select Category...</option>
                            {CATEGORY_OF_STAFF.map((c) => (
                              <option key={c.value} value={c.value}>
                                {c.label}
                              </option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>
                    </Row>

                    {/* Reason for Request */}
                    <Row className="mb-4">
                      <Col lg={4}>
                        <Form.Group controlId="reasonForRequest">
                          <Form.Label className="fw-bold">Reason for Request</Form.Label>
                          <Form.Select
                            value={reasonForRequest}
                            onChange={(e) => setReasonForRequest(e.target.value)}
                            required
                            size="lg"
                          >
                            <option value="">Select Reason...</option>
                            {REASON_FOR_REQUEST.map((r) => (
                              <option key={r.value} value={r.value}>
                                {r.label}
                              </option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>

                      <Col lg={8}>
                        <Form.Group controlId="reasonForRequestDetails">
                          <Form.Label className="fw-bold">Reason for Request Details</Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={2}
                            value={reasonForRequestDetails}
                            onChange={(e) => setReasonForRequestDetails(e.target.value)}
                            placeholder="Provide details about why this shift is needed..."
                            required
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    {/* Options Row */}
                    <Row className="mb-4">
                      <Col lg={6}>
                        <div className="border rounded p-3 bg-light">
                          <Form.Label className="fw-bold d-block">Options</Form.Label>
                          <Row>
                            <Col lg={4}>
                              <Form.Group controlId="escort" className="mb-3">
                                <Form.Check
                                  type="checkbox"
                                  label={<span className="fw-bold">Escort Required</span>}
                                  checked={escort}
                                  onChange={(e) => setEscort(e.target.checked)}
                                />
                              </Form.Group>
                            </Col>
                            <Col lg={4}>
                              <Form.Group controlId="special">
                                <Form.Check
                                  type="checkbox"
                                  label={<span className="fw-bold">Special</span>}
                                  checked={special}
                                  onChange={(e) => setSpecial(e.target.checked)}
                                />
                              </Form.Group>
                            </Col>
                            <Col lg={4}>
                              <Form.Group controlId="eccNumber">
                                <Form.Label className="fw-bold">ECC Number</Form.Label>
                                <Form.Control
                                  type="text"
                                  value={eccNumber}
                                  onChange={(e) => setEccNumber(e.target.value)}
                                  placeholder="Enter ECC number..."
                                  size="sm"
                                />
                              </Form.Group>
                            </Col>
                          </Row>
                        </div>
                      </Col>

                      <Col lg={6}>
                        <div className="border rounded p-3 bg-light">
                          <Form.Label className="fw-bold d-block">Requestor Information</Form.Label>
                          <Row>
                            <Col lg={6}>
                              <Form.Group controlId="requestorName">
                                <Form.Label className="fw-bold">Name</Form.Label>
                                <Form.Control
                                  type="text"
                                  value={userInfo?.name || ''}
                                  readOnly
                                  disabled
                                  size="sm"
                                />
                              </Form.Group>
                            </Col>
                            <Col lg={6}>
                              <Form.Group controlId="requestorEmail">
                                <Form.Label className="fw-bold">Email</Form.Label>
                                <Form.Control
                                  type="email"
                                  value={userInfo?.email || ''}
                                  readOnly
                                  disabled
                                  size="sm"
                                />
                              </Form.Group>
                            </Col>
                          </Row>
                        </div>
                      </Col>
                    </Row>

                    {/* Special Requirements and Comments */}
                    <Row className="mb-4">
                      <Col lg={6}>
                        <Form.Group controlId="specialRequirements">
                          <Form.Label className="fw-bold">Special Requirements</Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={3}
                            value={specialRequirements}
                            onChange={(e) => setSpecialRequirements(e.target.value)}
                            placeholder="Any special equipment, training, or requirements..."
                          />
                        </Form.Group>
                      </Col>

                      <Col lg={6}>
                        <Form.Group controlId="comments">
                          <Form.Label className="fw-bold">Comments</Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={3}
                            value={comments}
                            onChange={(e) => setComments(e.target.value)}
                            placeholder="Additional notes or comments..."
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                  </Tab>

                  <Tab eventKey="schedule" title="Schedule">
                    <Row className="mb-4">
                      <Col lg={12}>
                        <Form.Group controlId="shiftPreset">
                          <Form.Label className="fw-bold">Shift Time Preset</Form.Label>
                          <Row className="g-2">
                            {Object.entries(SHIFT_PRESETS).map(([key, preset]) => (
                              <Col lg={3} md={4} sm={6} key={key}>
                                <div 
                                  className={`border rounded p-2 cursor-pointer ${selectedPreset === key ? 'bg-primary text-white' : 'bg-light'}`}
                                  onClick={() => setSelectedPreset(key)}
                                  style={{cursor: 'pointer'}}
                                >
                                  <Form.Check
                                    type="radio"
                                    id={`preset-${key}`}
                                    label={preset.label}
                                    name="shiftPreset"
                                    checked={selectedPreset === key}
                                    onChange={() => {}}
                                    className="mb-0"
                                  />
                                </div>
                              </Col>
                            ))}
                          </Row>
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row className="mb-4">
                      <Col lg={3}>
                        <Form.Group controlId="startDate">
                          <Form.Label className="fw-bold">Start Date</Form.Label>
                          <Form.Control
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            required
                            size="lg"
                          />
                        </Form.Group>
                      </Col>

                      <Col lg={3}>
                        <Form.Group controlId="endDate">
                          <Form.Label className="fw-bold">End Date</Form.Label>
                          <Form.Control
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            min={startDate}
                            required
                            size="lg"
                          />
                          <Form.Text className="text-muted">
                            Same day unless overnight shift
                          </Form.Text>
                        </Form.Group>
                      </Col>

                      <Col lg={3}>
                        <Form.Group controlId="startTime">
                          <Form.Label className="fw-bold">Start Time</Form.Label>
                          <Form.Control
                            type="time"
                            value={startTime}  
                            onChange={(e) => setStartTime(e.target.value)}  
                            required
                            size="lg"
                          />
                        </Form.Group>
                      </Col>

                      <Col lg={3}>
                        <Form.Group controlId="endTime">
                          <Form.Label className="fw-bold">End Time</Form.Label>
                          <Form.Control
                            type="time"
                            value={endTime} 
                            onChange={(e) => setEndTime(e.target.value)}  
                            required
                            size="lg"
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row className="mb-4">
                      <Col lg={3}>
                        <Form.Group controlId="numberOfShifts">
                          <Form.Label className="fw-bold">Shifts per Day</Form.Label>
                          <Form.Control
                            type="number"
                            min="1"
                            max="10"
                            value={numberOfShifts}
                            onChange={(e) => setNumberOfShifts(Math.max(1, parseInt(e.target.value) || 1))}
                            size="lg"
                          />
                          <Form.Text className="text-muted">
                            Identical shifts per day
                          </Form.Text>
                        </Form.Group>
                      </Col>

                      <Col lg={9}>
                        <Form.Group controlId="recurrenceType">
                          <Form.Label className="fw-bold">Recurrence Pattern</Form.Label>
                          <Row className="g-2">
                            {Object.values(RECURRENCE_OPTIONS).map((option) => (
                              <Col lg={3} md={4} sm={6} key={option.value}>
                                <div 
                                  className={`border rounded p-2 cursor-pointer ${recurrenceType === option.value ? 'bg-primary text-white' : 'bg-light'}`}
                                  onClick={() => setRecurrenceType(option.value)}
                                  style={{cursor: 'pointer'}}
                                >
                                  <Form.Check
                                    type="radio"
                                    id={`recurrence-${option.value}`}
                                    label={option.label}
                                    name="recurrenceType"
                                    checked={recurrenceType === option.value}
                                    onChange={() => {}}
                                    className="mb-0"
                                  />
                                </div>
                              </Col>
                            ))}
                          </Row>
                        </Form.Group>
                      </Col>
                    </Row>

                    {recurrenceType !== 'NONE' && (
                      <>
                        <Row className="mb-4">
                          <Col lg={6}>
                            <Form.Group controlId="recurrenceEndDate">
                              <Form.Label className="fw-bold">Recurrence End Date</Form.Label>
                              <Form.Control
                                type="date"
                                value={recurrenceEndDate}
                                onChange={(e) => setRecurrenceEndDate(e.target.value)}
                                min={startDate}
                                size="lg"
                              />
                              <Form.Text className="text-muted">
                                Leave empty for no end date
                              </Form.Text>
                            </Form.Group>
                          </Col>

                          <Col lg={6}>
                            <Form.Group controlId="recurrenceOccurrences">
                              <Form.Label className="fw-bold">Number of Occurrences</Form.Label>
                              <Form.Control
                                type="number"
                                min="1"
                                max="365"
                                value={recurrenceOccurrences}
                                onChange={(e) => setRecurrenceOccurrences(Math.max(1, parseInt(e.target.value) || 1))}
                                size="lg"
                              />
                              <Form.Text className="text-muted">
                                Leave as 1 for unlimited (until end date)
                              </Form.Text>
                            </Form.Group>
                          </Col>
                        </Row>

                        <Row className="mb-4">
                          <Col lg={12}>
                            <Form.Label className="fw-bold d-block mb-2">Repeat On Days</Form.Label>
                            <div className="d-flex flex-wrap gap-3 border rounded p-3 bg-light">
                              {DAYS_OF_WEEK.map((day) => (
                                <div key={day.value} className="flex-grow-1" style={{minWidth: '120px'}}>
                                  <Form.Check
                                    type="checkbox"
                                    id={`day-${day.value}`}
                                    label={<span className="fw-bold">{day.label}</span>}
                                    checked={selectedDays.includes(day.value)}
                                    onChange={() => handleDayToggle(day.value)}
                                  />
                                </div>
                              ))}
                            </div>
                            <Form.Text className="text-muted">
                              Select which days of the week to repeat the shift
                            </Form.Text>
                          </Col>
                        </Row>
                      </>
                    )}

                    <Row className="mb-4">
                      <Col lg={12}>
                        <div className="alert alert-primary">
                          <div className="d-flex align-items-center">
                            <div className="me-3">
                              <i className="bi bi-info-circle fs-4"></i>
                            </div>
                            <div>
                              <strong className="fs-5">Summary:</strong>
                              <div className="mt-1">
                                This will create <strong className="fs-5">{calculateTotalShifts()}</strong> pre-shift request{calculateTotalShifts() > 1 ? 's' : ''}
                                {recurrenceType !== 'NONE' && <span> with <strong>{recurrenceType.toLowerCase()}</strong> recurrence</span>}
                                {selectedDays.length > 0 && recurrenceType !== 'NONE' && <span> on <strong>{selectedDays.length}</strong> selected days</span>}
                                {numberOfShifts > 1 && <span>, with <strong>{numberOfShifts}</strong> shift{numberOfShifts > 1 ? 's' : ''} per day</span>}
                              </div>
                            </div>
                          </div>
                        </div>
                      </Col>
                    </Row>
                  </Tab>
                </Tabs>

                <div className="d-flex justify-content-between pt-3 border-top">
                  <Button 
                    variant="secondary" 
                    onClick={() => navigate('/admin/ward-preshifts')}
                    size="lg"
                  >
                    Cancel
                  </Button>
                  <Button 
                    type="submit" 
                    variant="primary" 
                    size="lg"  
                    disabled={isSubmitting || loadingCreate}
                  >
                    {isSubmitting ? 'Processing...' : `Review ${calculateTotalShifts() > 1 ? `${calculateTotalShifts()} Requests` : 'Request'}`}
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Form>
        )}

        {/* Confirmation Modal */}
        <Modal 
          show={showConfirmation} 
          onHide={() => !isSubmitting && setShowConfirmation(false)}
          size="xl"
          backdrop="static"
          keyboard={!isSubmitting}
        >
          <Modal.Header closeButton={!isSubmitting}>
            <Modal.Title>
              Confirm Pre-Shift Requests ({shiftList.length} Requests)
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="alert alert-info mb-4">
              <div className="d-flex align-items-center">
                <div className="me-3">
                  <i className="bi bi-info-circle fs-4"></i>
                </div>
                <div>
                  <strong>Review Your Requests</strong>
                  <p className="mb-0 mt-1">
                    You are about to submit {shiftList.length} pre-shift request{shiftList.length > 1 ? 's' : ''} for approval.
                    Each request will need to be approved by an administrator.
                  </p>
                </div>
              </div>
            </div>

            <div className="table-responsive" style={{ maxHeight: '400px', overflowY: 'auto' }}>
              <Table striped bordered hover>
                <thead style={{ position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 1 }}>
                  <tr>
                    <th>#</th>
                    <th>Date</th>
                    <th>Day</th>
                    <th>Start Time</th>
                    <th>End Time</th>
                    <th>Duration</th>
                    <th>Type</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {shiftList.map((shift, index) => {
                    const isValid = isShiftValid(shift);
                    const isOvernight = isOvernightShift(shift);
                    
                    return (
                      <tr key={shift.id} className={!isValid ? 'table-warning' : ''}>
                        <td className="fw-bold">{index + 1}</td>
                        <td>
                          <Form.Control
                            type="date"
                            value={shift.startDate}
                            onChange={(e) => handleShiftUpdate(index, 'startDate', e.target.value)}
                            size="sm"
                            disabled={isSubmitting}
                          />
                        </td>
                        <td className="fw-bold">{getDayOfWeek(shift.startDate)}</td>
                        <td>
                          <Form.Control
                            type="time"
                            value={shift.startTime}
                            onChange={(e) => handleShiftUpdate(index, 'startTime', e.target.value)}
                            size="sm"
                            disabled={isSubmitting}
                          />
                        </td>
                        <td>
                          <Form.Control
                            type="time"
                            value={shift.endTime}
                            onChange={(e) => handleShiftUpdate(index, 'endTime', e.target.value)}
                            size="sm"
                            disabled={isSubmitting}
                          />
                        </td>
                        <td className="fw-bold">
                          {calculateDuration(shift.startDate, shift.startTime, shift.endDate, shift.endTime)}
                          {isOvernight && <span className="ms-1 text-primary">🌙</span>}
                        </td>
                        <td>
                          {!isValid ? (
                            <span className="badge bg-warning text-dark">Invalid</span>
                          ) : isOvernight ? (
                            <span className="badge bg-info">Overnight</span>
                          ) : (
                            <span className="badge bg-success">Valid</span>
                          )}
                        </td>
                        <td>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleRemoveShift(index)}
                            disabled={isSubmitting}
                          >
                            <FaTrash />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>

            <div className="mt-4">
              <Row>
                <Col md={6}>
                  <div className="border rounded p-3 bg-light">
                    <h6>Request Summary</h6>
                    <ul className="mb-0">
                      <li>Total Requests: <strong>{shiftList.length}</strong></li>
                      <li>Overnight Shifts: <strong>{shiftList.filter(isOvernightShift).length}</strong></li>
                      <li>Invalid Shifts: <strong>{shiftList.filter(s => !isShiftValid(s)).length}</strong></li>
                    </ul>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="border rounded p-3 bg-light">
                    <h6>Location & Category</h6>
                    <ul className="mb-0">
                      <li><strong>Location:</strong> {hospitalLocations?.find(l => l._id === hospitalLocationId)?.wardDepartmentArea}</li>
                      <li><strong>Category:</strong> {categoryOfStaff}</li>
                      <li><strong>Reason:</strong> {reasonForRequest}</li>
                    </ul>
                  </div>
                </Col>
              </Row>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button 
              variant="secondary" 
              onClick={() => setShowConfirmation(false)}
              disabled={isSubmitting}
            >
              Back to Edit
            </Button>
            <Button 
              variant="primary" 
              onClick={createAllPreShifts}
              disabled={isSubmitting || shiftList.length === 0 || shiftList.some(s => !isShiftValid(s))}
            >
              {isSubmitting ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Creating {shiftList.length} Requests...
                </>
              ) : (
                <>
                  <i className="bi bi-check-circle me-1"></i>
                  Submit {shiftList.length} Request{shiftList.length > 1 ? 's' : ''} for Approval
                </>
              )}
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>
    </>
  );
};

export default WardPreShiftCreateScreen;